import React, { useEffect, useRef, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import { TextField } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import FontIcon from "react-md/lib/FontIcons";
import moment from "moment";
import LTRService from "../service/LTRService";
import {
  OUTCOME_OPTIONS,
  OUTCOME_LABEL_MAP,
  ACTION_OPTIONS,
  ROLE_DOCTOR,
  ROLE_COORDINATOR,
  ROLE_LABEL_MAP,
  FOLLOW_UP_PRESETS,
} from "../constants";

const ltrService = new LTRService();

const fmtDateTime = (d) => (d ? moment(d).format("D MMM YYYY, h:mm A") : "-");

// Shared frame for the case dialogs: title, body, error, and a secondary /
// primary pair. `onSubmit` returns a promise; the frame owns the saving state
// so a double click cannot submit twice. `tone` picks the primary colour the
// design uses for that dialog ("orange" or "purple").
function CaseDialog({
  open,
  title,
  titleBadge,
  subtitle,
  submitLabel,
  cancelLabel = "Cancel",
  canSubmit,
  onSubmit,
  onClose,
  footerLeft,
  tone = "orange",
  maxWidth = "sm",
  children,
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const savingRef = useRef(false);

  useEffect(() => {
    if (open) {
      setError("");
      setSaving(false);
      savingRef.current = false;
    }
  }, [open]);

  const close = () => {
    if (!savingRef.current) onClose();
  };

  const submit = () => {
    if (savingRef.current || !canSubmit) return;
    savingRef.current = true;
    setSaving(true);
    setError("");
    Promise.resolve(onSubmit())
      .then((keepOpen) => {
        savingRef.current = false;
        setSaving(false);
        if (keepOpen !== true) onClose();
      })
      .catch((err) => {
        savingRef.current = false;
        setSaving(false);
        setError(
          err?.message || err?.error || "Something went wrong. Please try again.",
        );
      });
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{ className: "ltr-modal" }}
    >
      <div className="ltr-modal-header">
        <div>
          <h3>
            {title}
            {titleBadge && <span className="ltr-modal-badge">{titleBadge}</span>}
          </h3>
          {subtitle && <p>{subtitle}</p>}
        </div>
        <button type="button" className="ltr-modal-close" onClick={close} aria-label="Close">
          ✕
        </button>
      </div>
      <div className="ltr-modal-body">{children}</div>
      <div className="ltr-modal-footer">
        {footerLeft && <span className="ltr-modal-footer-left">{footerLeft}</span>}
        {error && <span className="ltr-modal-error">{error}</span>}
        <button type="button" className="ltr-btn" onClick={close} disabled={saving}>
          {cancelLabel}
        </button>
        <button
          type="button"
          className={`ltr-btn ${tone === "purple" ? "purple" : "primary"}`}
          onClick={submit}
          disabled={!canSubmit || saving}
        >
          {saving ? "Saving..." : submitLabel}
        </button>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// People pickers
// ---------------------------------------------------------------------------

// The LTR team, from the service's doctors and coordinators collections.
// Fetched once per page load and shared by every dialog that picks a person.
let coordinatorsCache = null;
let doctorsCache = null;

const openCasesLabel = (n) => `${n || 0} open case${n === 1 ? "" : "s"}`;

// Team member -> picker option. Doctor and coordinator ids are separate series,
// so an option is only ever compared within its own role.
const toOption = (m, detail) => ({
  id: m.id,
  label: m.name || `${m.role === ROLE_DOCTOR ? "Doctor" : "Employee"} ${m.id}`,
  sub: [detail, openCasesLabel(m.openCases)].filter(Boolean).join(" · "),
});

function useTeam(open, cache, setCache, fetch, detailOf, what) {
  const [members, setMembers] = useState(cache());
  useEffect(() => {
    if (!open || cache()) return;
    fetch(
      (res) => {
        setCache((Array.isArray(res) ? res : []).filter((m) => m && m.id).map((m) => toOption(m, detailOf(m))));
        setMembers(cache());
      },
      (err) => {
        console.error(`Error fetching LTR ${what}:`, err);
        setMembers([]);
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  return members;
}

function useCoordinators(open) {
  return useTeam(
    open,
    () => coordinatorsCache,
    (v) => (coordinatorsCache = v),
    ltrService.fetchCoordinators,
    (m) => m.team,
    "coordinators",
  );
}

function useDoctors(open) {
  return useTeam(
    open,
    () => doctorsCache,
    (v) => (doctorsCache = v),
    ltrService.fetchDoctors,
    (m) => m.speciality,
    "doctors",
  );
}

function PersonPicker({ options, value, onChange, placeholder, roleLabel, disabledId }) {
  return (
    <Autocomplete
      options={options || []}
      loading={options === null}
      value={value}
      onChange={(e, v) => onChange(v)}
      getOptionLabel={(o) => (o ? `${o.label}${roleLabel ? ` · ${roleLabel}` : ""}` : "")}
      getOptionSelected={(o, v) => o.id === v.id}
      getOptionDisabled={(o) => o.id === disabledId}
      renderOption={(o) => (
        <div className="ltr-assignee-option">
          <span>{o.label}</span>
          {o.id === disabledId ? <small>Current assignee</small> : o.sub && <small>{o.sub}</small>}
        </div>
      )}
      renderInput={(params) => (
        <TextField {...params} variant="outlined" size="small" placeholder={placeholder} />
      )}
    />
  );
}

// ---------------------------------------------------------------------------
// Follow-up window (04.4 / 04.6)
// ---------------------------------------------------------------------------

const DATE_FORMAT = "YYYY-MM-DD";
const TIME_FORMAT = "HH:mm";

// Presets from now, or a due date and time set by hand. `value` is an ISO
// string, or "" until something is chosen. Dialog content unmounts on close,
// so the picker starts fresh each time it opens.
function FollowUpWindow({ value, onChange }) {
  const [preset, setPreset] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");

  const applyPreset = (hours) => {
    const due = moment().add(hours, "hours");
    setPreset(String(hours));
    setDate(due.format(DATE_FORMAT));
    setTime(due.format(TIME_FORMAT));
    onChange(due.toISOString());
  };

  const applyManual = (d, t) => {
    setPreset("");
    setDate(d);
    setTime(t);
    if (d && t) {
      const due = moment(`${d} ${t}`, `${DATE_FORMAT} ${TIME_FORMAT}`);
      onChange(due.isValid() ? due.toISOString() : "");
    } else {
      onChange("");
    }
  };

  return (
    <div className="ltr-followup-window">
      <div className="ltr-followup-caption">Choose a preset</div>
      <div className="ltr-chip-row">
        {FOLLOW_UP_PRESETS.map((p) => (
          <button
            key={p.hours}
            type="button"
            className={`ltr-chip soft${preset === String(p.hours) ? " selected" : ""}`}
            onClick={() => applyPreset(p.hours)}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="ltr-followup-or">
        <span>Or set the date yourself</span>
      </div>
      <div className="ltr-field-grid">
        <div>
          <label className="ltr-field-label inline">
            Due date<span className="ltr-required">*</span>
          </label>
          <input
            type="date"
            className="ltr-input full"
            value={date}
            min={moment().format(DATE_FORMAT)}
            onChange={(e) => applyManual(e.target.value, time)}
          />
        </div>
        <div>
          <label className="ltr-field-label inline">
            Due time<span className="ltr-required">*</span>
          </label>
          <input
            type="time"
            className="ltr-input full"
            value={time}
            onChange={(e) => applyManual(date, e.target.value)}
          />
        </div>
      </div>
      {value && moment(value).isBefore(moment()) && (
        <p className="ltr-field-hint error">The due time is in the past.</p>
      )}
    </div>
  );
}

const validFollowUp = (v) => !!v && moment(v).isAfter(moment());

// ---------------------------------------------------------------------------
// Assign action — step 3 (04.4 with a follow-up window, 04.5 without)
// ---------------------------------------------------------------------------

export function AssignActionDialog({
  open,
  appointment,
  withFollowUp,
  defaultAssignee,
  onClose,
  onSubmit,
}) {
  const coordinators = useCoordinators(open);
  const [actions, setActions] = useState([]);
  const [assignee, setAssignee] = useState(null);
  const [followUp, setFollowUp] = useState("");
  const [note, setNote] = useState("");
  const startsAt = useRef(new Date());

  useEffect(() => {
    if (!open) return;
    startsAt.current = new Date();
    setActions(Array.isArray(appointment?.actions) ? appointment.actions : []);
    setNote(appointment?.actionNote || "");
    setFollowUp("");
    setAssignee(defaultAssignee || null);
  }, [open, appointment, defaultAssignee]);

  // Preselect the coordinator once the list is in, if one was suggested.
  useEffect(() => {
    if (!open || assignee || !defaultAssignee || !coordinators) return;
    const match = coordinators.find((e) => e.id === defaultAssignee.id);
    if (match) setAssignee(match);
  }, [open, coordinators, defaultAssignee, assignee]);

  const toggle = (value) =>
    setActions((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );

  const canSubmit =
    actions.length > 0 && !!assignee && (!withFollowUp || validFollowUp(followUp));

  return (
    <CaseDialog
      open={open}
      title="Step 3 · Assign action"
      titleBadge="Step 3 of 3"
      submitLabel="Set actions"
      cancelLabel="Back"
      canSubmit={canSubmit}
      footerLeft={`${actions.length} action${actions.length === 1 ? "" : "s"} selected`}
      onSubmit={() =>
        onSubmit({
          actions: [...actions].sort((a, b) => a - b),
          note: note.trim(),
          assigneeId: assignee.id,
          followUpDate: withFollowUp ? followUp : null,
        })
      }
      onClose={onClose}
    >
      <div className="ltr-check-list">
        {ACTION_OPTIONS.map((o) => {
          const checked = actions.includes(o.value);
          return (
            <label key={o.value} className={`ltr-check-row${checked ? " checked" : ""}`}>
              <input type="checkbox" checked={checked} onChange={() => toggle(o.value)} />
              <span>
                {o.value}. {o.label}
              </span>
            </label>
          );
        })}
      </div>

      <div className="ltr-field-grid">
        <div>
          <label className="ltr-field-label inline">
            Assign to<span className="ltr-required">*</span>
          </label>
          <PersonPicker
            options={coordinators}
            value={assignee}
            onChange={setAssignee}
            roleLabel={ROLE_LABEL_MAP[ROLE_COORDINATOR]}
            placeholder="Search coordinators..."
          />
        </div>
        <div>
          <label className="ltr-field-label inline">Starts</label>
          <input className="ltr-input full readonly" readOnly value={fmtDateTime(startsAt.current)} />
        </div>
      </div>

      {withFollowUp && (
        <>
          <label className="ltr-field-label">
            Follow-up window<span className="ltr-required">*</span>
          </label>
          <FollowUpWindow value={followUp} onChange={setFollowUp} />
        </>
      )}

      <label className="ltr-field-label">Note (optional)</label>
      <textarea
        className="ltr-textarea short"
        value={note}
        maxLength={500}
        placeholder="e.g. HbA1c + lipid ordered via Redcliffe, home collection on 26 Aug."
        onChange={(e) => setNote(e.target.value)}
      />
    </CaseDialog>
  );
}

// ---------------------------------------------------------------------------
// Update follow-up date (04.6) — a dated task for a coordinator
// ---------------------------------------------------------------------------

export function FollowUpTaskDialog({
  open,
  title = "Update follow up date",
  defaultAssignee,
  defaultRole,
  onClose,
  onSubmit,
}) {
  // The follow-up goes to a doctor or a coordinator; it opens on whoever holds
  // the case now, in their role, or on defaultRole when nobody does.
  const initialRole = defaultAssignee
    ? defaultAssignee.role === ROLE_DOCTOR
      ? ROLE_DOCTOR
      : ROLE_COORDINATOR
    : defaultRole || ROLE_COORDINATOR;
  const [role, setRole] = useState(initialRole);
  const doctors = useDoctors(open && role === ROLE_DOCTOR);
  const coordinators = useCoordinators(open && role === ROLE_COORDINATOR);
  const people = role === ROLE_DOCTOR ? doctors : coordinators;
  const [assignee, setAssignee] = useState(null);
  const [followUp, setFollowUp] = useState("");
  const [task, setTask] = useState("");
  const startsAt = useRef(new Date());

  useEffect(() => {
    if (!open) return;
    startsAt.current = new Date();
    setRole(initialRole);
    setFollowUp("");
    setTask("");
    setAssignee(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Preselect the current holder, but only in their own role: doctor and
  // coordinator ids are separate series, so the same id in the other list is
  // somebody else.
  useEffect(() => {
    if (!open || assignee || !defaultAssignee || defaultAssignee.role !== role || !people) return;
    const match = people.find((e) => e.id === defaultAssignee.id);
    if (match) setAssignee(match);
  }, [open, people, defaultAssignee, assignee, role]);

  const roleName = ROLE_LABEL_MAP[role];

  return (
    <CaseDialog
      open={open}
      title={title}
      submitLabel="Assign case"
      tone="purple"
      canSubmit={!!assignee && validFollowUp(followUp) && !!task.trim()}
      onSubmit={() =>
        onSubmit({
          employeeId: assignee.id,
          role,
          task: task.trim(),
          followUpDate: followUp,
        })
      }
      onClose={onClose}
    >
      <label className="ltr-field-label inline">Assign to</label>
      <div className="ltr-chip-row">
        {[ROLE_DOCTOR, ROLE_COORDINATOR].map((r) => (
          <button
            key={r}
            type="button"
            className={`ltr-chip${role === r ? " selected" : ""}`}
            onClick={() => {
              if (r === role) return;
              setRole(r);
              setAssignee(null);
            }}
          >
            {ROLE_LABEL_MAP[r]}
          </button>
        ))}
      </div>

      <div className="ltr-field-grid">
        <div>
          <label className="ltr-field-label inline">
            {roleName}
            <span className="ltr-required">*</span>
          </label>
          <PersonPicker
            options={people}
            value={assignee}
            onChange={setAssignee}
            roleLabel={roleName}
            placeholder={`Search ${role === ROLE_DOCTOR ? "doctors" : "coordinators"}...`}
          />
        </div>
        <div>
          <label className="ltr-field-label inline">Starts</label>
          <input className="ltr-input full readonly" readOnly value={fmtDateTime(startsAt.current)} />
        </div>
      </div>

      <label className="ltr-field-label">
        Follow-up window<span className="ltr-required">*</span>
      </label>
      <FollowUpWindow value={followUp} onChange={setFollowUp} />

      <label className="ltr-field-label">
        Task for the {roleName.toLowerCase()}
        <span className="ltr-required">*</span>
      </label>
      <textarea
        className="ltr-textarea short"
        value={task}
        maxLength={500}
        placeholder="e.g. Track the diagnostic report and upload it when it arrives."
        onChange={(e) => setTask(e.target.value)}
      />
    </CaseDialog>
  );
}

// ---------------------------------------------------------------------------
// Closing a case (05.1 – 05.3)
// ---------------------------------------------------------------------------

const OUTCOME_HINTS = {
  1: "The action was completed and the risk was addressed.",
  2: "The case ran its course but the risk was not addressed.",
  3: "Prism flagged the patient but the risk was not real.",
  4: "The patient declined to continue with the care team.",
};

export function CloseCaseDialog({ open, onClose, onSubmit }) {
  const [outcome, setOutcome] = useState(0);
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) {
      setOutcome(0);
      setReason("");
    }
  }, [open]);

  return (
    <CaseDialog
      open={open}
      title="Close case"
      submitLabel="Close case"
      canSubmit={!!outcome}
      onSubmit={() => onSubmit({ outcome, reason: reason.trim() })}
      onClose={onClose}
    >
      <div className="ltr-radio-list">
        {OUTCOME_OPTIONS.map((o, i) => (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={outcome === o.value}
            className={`ltr-radio-row${outcome === o.value ? " selected" : ""}`}
            onClick={() => setOutcome(o.value)}
          >
            <span className="ltr-radio" />
            <span>
              <strong>
                {i + 1}. {o.label}
              </strong>
              <small>{OUTCOME_HINTS[o.value]}</small>
            </span>
          </button>
        ))}
      </div>
      <label className="ltr-field-label">Closing note (optional)</label>
      <textarea
        className="ltr-textarea short"
        value={reason}
        maxLength={1000}
        placeholder="e.g. Diagnostic report received and reviewed; BP back within range."
        onChange={(e) => setReason(e.target.value)}
      />
    </CaseDialog>
  );
}

function SummaryTable({ rows }) {
  return (
    <div className="ltr-summary-table">
      {rows
        .filter(([, value]) => value)
        .map(([label, value]) => (
          <div key={label} className="ltr-summary-row">
            <span>{label}</span>
            <strong>{value}</strong>
          </div>
        ))}
    </div>
  );
}

// 05.2 — shown once, right after closing.
export function CaseClosedDialog({ open, caseRecord, patientName, closedByName, durationLabel, onStay, onBack }) {
  if (!caseRecord) return null;
  return (
    <Dialog open={open} onClose={onStay} maxWidth="xs" fullWidth PaperProps={{ className: "ltr-modal" }}>
      <div className="ltr-modal-header">
        <h3 className="ltr-modal-title-check">
          <span className="ltr-check-badge">
            <FontIcon>check</FontIcon>
          </span>
          Case closed
        </h3>
      </div>
      <div className="ltr-modal-body">
        <SummaryTable
          rows={[
            ["Case", `${caseRecord.ltrId}${patientName ? ` · ${patientName}` : ""}`],
            ["Closed as", OUTCOME_LABEL_MAP[caseRecord.closureOutcome]],
            ["Closed by", `${closedByName || "-"} · ${fmtDateTime(caseRecord.closedAt)}`],
            ["Case duration", durationLabel],
          ]}
        />
      </div>
      <div className="ltr-modal-footer">
        <button type="button" className="ltr-btn" onClick={onStay}>
          Stay on case
        </button>
        <button type="button" className="ltr-btn purple" onClick={onBack}>
          Back to dashboard
        </button>
      </div>
    </Dialog>
  );
}

// 05.3 — opened from the "Closed" status pill.
export function ClosureDetailsDialog({
  open,
  caseRecord,
  patientName,
  closedByName,
  closedByRole,
  durationLabel,
  actionLabel,
  onClose,
  onShowDetails,
}) {
  if (!caseRecord) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ className: "ltr-modal" }}>
      <div className="ltr-modal-header">
        <h3 className="ltr-modal-title-check">
          <span className="ltr-closed-chip">
            <FontIcon>check_circle</FontIcon>
            Closed
          </span>
          {OUTCOME_LABEL_MAP[caseRecord.closureOutcome] || "Closed"}
        </h3>
        <button type="button" className="ltr-modal-close" onClick={onClose} aria-label="Close">
          ✕
        </button>
      </div>
      <div className="ltr-modal-body">
        <SummaryTable
          rows={[
            ["Case", `${caseRecord.ltrId}${patientName ? ` · ${patientName}` : ""}`],
            ["Closed by", `${closedByName || "-"}${closedByRole ? ` · ${closedByRole}` : ""}`],
            ["Closed on", fmtDateTime(caseRecord.closedAt)],
            ["Case duration", durationLabel],
            ["Action at closure", actionLabel],
          ]}
        />
        {caseRecord.closureReason && (
          <>
            <div className="ltr-section-caption">Closing note</div>
            <div className="ltr-note-box">{caseRecord.closureReason}</div>
          </>
        )}
      </div>
      <div className="ltr-modal-footer">
        <button type="button" className="ltr-btn purple" onClick={onShowDetails}>
          Show details
        </button>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Chat / e-mail (06.2 — not in this release) and call remarks
// ---------------------------------------------------------------------------

export function ComingSoonDialog({ open, kind, onClose, onCall }) {
  const isChat = kind === "chat";
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ className: "ltr-modal" }}>
      <div className="ltr-modal-header">
        <h3>{isChat ? "Chat with patient" : "E-mail patient"}</h3>
        <button type="button" className="ltr-modal-close" onClick={onClose} aria-label="Close">
          ✕
        </button>
      </div>
      <div className="ltr-modal-body">
        <div className="ltr-coming-soon">
          <span className="ltr-coming-soon-icon">
            <FontIcon>{isChat ? "chat_bubble_outline" : "mail_outline"}</FontIcon>
          </span>
          <span className="ltr-coming-soon-chip">Coming soon</span>
          <p>
            {isChat
              ? "In-app chat with the patient is not part of this release."
              : "E-mailing the patient from the case is not part of this release."}{" "}
            Call the patient for anything that cannot wait.
          </p>
        </div>
      </div>
      <div className="ltr-modal-footer">
        <button type="button" className="ltr-btn" onClick={onClose}>
          Close
        </button>
        <button type="button" className="ltr-btn primary" onClick={onCall}>
          <FontIcon>call</FontIcon>
          Call patient
        </button>
      </div>
    </Dialog>
  );
}

export function RemarkDialog({ open, callLabel, initial, onClose, onSubmit }) {
  const [remark, setRemark] = useState("");
  useEffect(() => {
    if (open) setRemark(initial || "");
  }, [open, initial]);
  return (
    <CaseDialog
      open={open}
      title="Call remark"
      subtitle={callLabel}
      submitLabel="Save remark"
      canSubmit={remark.trim() !== (initial || "").trim()}
      onSubmit={() => onSubmit(remark.trim())}
      onClose={onClose}
    >
      <textarea
        className="ltr-textarea"
        value={remark}
        maxLength={500}
        placeholder="e.g. History taken; diagnostic test advised"
        onChange={(e) => setRemark(e.target.value)}
      />
    </CaseDialog>
  );
}
